import { build_web_analytics_settings } from './settings';
import { build_base_analytics_event, initialize_analytics_session } from './session';
import {
    analytics_consent_allows_capture,
    element_or_parent_has_do_not_track_attribute,
    read_persistent_do_not_track_flag
} from './privacy';
import { should_skip_auto_click_capture_for_target } from './element';
import { analytics_captures_disabled } from './state';

function push_event_to_pre_init_queue(data_hub_instance, analytics_event) {
    const analytics_state = data_hub_instance.analytics_state;
    if (!analytics_state || !analytics_event) {
        return null;
    }
    analytics_state.pre_init_queue = analytics_state.pre_init_queue || [];
    analytics_state.pre_init_queue.push(analytics_event);
    const maximum_queue_size = data_hub_instance.web_settings?.max_pre_init_queue_size || 50;
    while (analytics_state.pre_init_queue.length > maximum_queue_size) {
        analytics_state.pre_init_queue.shift();
    }
    return analytics_event;
}

function build_minimal_early_click_payload(dom_event) {
    const target_element = dom_event?.target;
    if (!target_element || typeof target_element.getAttribute !== 'function') {
        return {};
    }
    return {
        event_sub_type: target_element.getAttribute('data-hub-event-sub-type') || null,
        web_event_data: {
            early_auto_event: true,
            early_dom_event: 'click',
            early_tag_name: target_element.tagName?.toLowerCase() || null,
            early_attr_id: target_element.id || null,
            early_attr_role: target_element.getAttribute('role') || null
        }
    };
}

function attach_early_auto_event_listeners(data_hub_instance) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }
    if (!data_hub_instance.web_settings?.capture_early_auto_events) {
        return;
    }
    const analytics_state = data_hub_instance.analytics_state;
    analytics_state.early_cleanup_callbacks = analytics_state.early_cleanup_callbacks || [];

    if (data_hub_instance.web_settings.auto_click) {
        const early_click_listener = (dom_event) => {
            if (
                data_hub_instance.analytics_state?.is_full_analytics_loaded
                || !analytics_consent_allows_capture(data_hub_instance)
                || should_skip_auto_click_capture_for_target(dom_event.target)
                || element_or_parent_has_do_not_track_attribute(dom_event.target)
            ) {
                return;
            }
            data_hub_instance.capture('$click', build_minimal_early_click_payload(dom_event));
        };
        document.addEventListener('click', early_click_listener, true);
        analytics_state.early_cleanup_callbacks.push(() => document.removeEventListener('click', early_click_listener, true));
    }

    if (data_hub_instance.web_settings.auto_scroll) {
        const early_scroll_listener = () => {
            if (data_hub_instance.analytics_state?.is_full_analytics_loaded || !analytics_consent_allows_capture(data_hub_instance)) {
                return;
            }
            data_hub_instance.capture('$scroll', {
                web_event_data: {
                    early_auto_event: true,
                    early_dom_event: 'scroll'
                }
            });
            window.removeEventListener('scroll', early_scroll_listener);
        };
        window.addEventListener('scroll', early_scroll_listener, { passive: true });
        analytics_state.early_cleanup_callbacks.push(() => window.removeEventListener('scroll', early_scroll_listener));
    }
}

/**
 * Initializes the tiny analytics bootstrap that lives in the main bundle. It creates session
 * identifiers, installs a lightweight capture stub, and optionally captures a first click or
 * scroll before the full lazy-loaded analytics module is available.
 *
 * @param {Object} data_hub_instance - DataHub instance being initialized.
 * @param {Object} config - DataHub constructor config.
 * @returns {Object|null} Analytics state, or null for invalid instances.
 */
export function init_analytics_bootstrap(data_hub_instance, config = {}) {
    if (!data_hub_instance || typeof data_hub_instance !== 'object') {
        return null;
    }

    data_hub_instance.web_settings = build_web_analytics_settings(config, data_hub_instance.web_settings);

    // Persistent DNT (set by a prior forget_user under this organization_id) overrides any
    // in-config web_settings. Do not initialize the session — that would write a fresh
    // session_id to sessionStorage. Install a no-op capture so any data_hub.capture() call
    // from host code remains valid. Identity / forms / JWT flows are unaffected; DNT only
    // suppresses analytics.
    if (read_persistent_do_not_track_flag(data_hub_instance)) {
        data_hub_instance.analytics_state = data_hub_instance.analytics_state || {};
        data_hub_instance.analytics_state.captures_disabled = true;
        data_hub_instance.analytics_state.pre_init_queue = [];
        data_hub_instance.analytics_state.queue = [];
        data_hub_instance.web_settings.enabled = false;
        data_hub_instance.capture = () => null;
        data_hub_instance.capture.flush = async () => null;
        return data_hub_instance.analytics_state;
    }

    const analytics_state = initialize_analytics_session(data_hub_instance);
    if (!analytics_state) {
        return null;
    }
    analytics_state.pre_init_queue = analytics_state.pre_init_queue || [];
    analytics_state.queue = analytics_state.queue || [];
    analytics_state.is_full_analytics_loaded = false;
    analytics_state.init_started_at = analytics_state.init_started_at || new Date().toISOString();

    data_hub_instance.capture = (event_name, event_properties = {}) => {
        if (analytics_captures_disabled(data_hub_instance)) {
            return null;
        }
        if (!data_hub_instance.web_settings?.enabled || !analytics_consent_allows_capture(data_hub_instance)) {
            analytics_state.pre_init_queue = [];
            return null;
        }
        if (!event_name || typeof event_name !== 'string') {
            data_hub_instance.error_function?.('data_hub.capture requires an event name string', null);
            return null;
        }
        const analytics_event = build_base_analytics_event(
            data_hub_instance,
            event_name,
            event_properties
        );
        if (!analytics_event) {
            return null;
        }
        return push_event_to_pre_init_queue(data_hub_instance, analytics_event);
    };

    data_hub_instance.capture.flush = async () => null;
    attach_early_auto_event_listeners(data_hub_instance);
    return analytics_state;
}